import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { TableList } from '../../components/validator/TableList';
import iconValidar from '../../assets/icon-validar.svg';
import FileSaver from 'file-saver';

export const ValidatedPage = () => {

    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();

    const [tableApprovedSign, setTableAprovedSign] = useState([]);
    const [tableFileInfo, setTableFileInfo] = useState([]);
    const [tableListInfo, setTableListInfo] = useState([]);
    const [tableSignInfo, setTableSignInfo] = useState([]);
    const [tableCertPath, setTableCertPath] = useState([]);
    const [tableRequiredAttr, setTableRequiredAttr] = useState([]);
    const [tableOptionalAttr, setTableOptionalAttr] = useState([]);

    const loadFullData = (state) => {
        const { report, trustedList = report.tls?.trustedList, signatures = state.report.signatures?.signature } = state;
        setTableAprovedSign([
            { label: t('validated.verification-date'), value: report.date.verificationDate },
            { label: t('validated.software-version'), value: report.software.version }
        ]);

        setTableFileInfo([
            { label: t('validated.file-name'), value: report.software.sourceFile },
            { label: t('validated.summary-sha256'), value: report.software.sourceFileHash },
            { label: t('validated.file-type'), value: report.initialReport.fileType },
            { label: t('validated.number-signatures'), value: report.initialReport.qtdSignatures },
            { label: t('validated.number-pinned-signatures'), value: report.initialReport.qtdAnchorsSign }
        ]);

        if (trustedList) {
            if (Array.isArray(trustedList)) {
                const listFullListInfo = [];
                trustedList.forEach(trust => {
                    listFullListInfo.push([
                        { label: t('validated.version'), value: trust.versionIdentifier },
                        { label: t('validated.sequence-number'), value: trust.sequenceNumber },
                        { label: t('validated.list-type'), value: trust.tslType },
                        { label: t('validated.operator-name'), value: trust.operatorName },
                        { label: t('validated.country-code'), value: trust.countryCode },
                        { label: t('validated.issue-date'), value: trust.issueDateTime },
                        { label: t('validated.next-update'), value: trust.nextUpdate },
                        { label: t('validated.list-validity'), value: trust.tlValid ? t('validated.valid') : t('validated.invalid') },
                        { label: t('validated.number-providers'), value: trust.trustServiceCount },
                        { label: t('validated.number-services'), value: trust.trustServiceProviderCount },
                        { label: t('validated.number-certificates'), value: trust.certificateCount },
                        { label: t('validated.community'), value: trust.communityRules.communityRule },
                        { label: t('validated.distribution-points'), value: trust.distributionPoints.distributionPoint },
                        { label: t('validated.error-message'), value: trust.errorMessages.errorMessage.join(' ') }
                    ]);
                });
                setTableListInfo(listFullListInfo);
            } else {
                setTableListInfo([[
                    { label: t('validated.version'), value: trustedList.versionIdentifier },
                    { label: t('validated.sequence-number'), value: trustedList.sequenceNumber },
                    { label: t('validated.list-type'), value: trustedList.tslType },
                    { label: t('validated.operator-name'), value: trustedList.operatorName },
                    { label: t('validated.country-code'), value: trustedList.countryCode },
                    { label: t('validated.issue-date'), value: trustedList.issueDateTime },
                    { label: t('validated.next-update'), value: trustedList.nextUpdate },
                    { label: t('validated.list-validity'), value: trustedList.tlValid ? t('validated.valid') : t('validated.invalid') },
                    { label: t('validated.number-providers'), value: trustedList.trustServiceCount },
                    { label: t('validated.number-services'), value: trustedList.trustServiceProviderCount },
                    { label: t('validated.number-certificates'), value: trustedList.certificateCount },
                    { label: t('validated.community'), value: trustedList.communityRules.communityRule },
                    { label: t('validated.distribution-points'), value: trustedList.distributionPoints.distributionPoint },
                    { label: t('validated.error-message'), value: trustedList.errorMessages.errorMessage.join(' ') }
                ]]);
            }
        }

        if (report.initialReport.qtdSignatures > 1) {

            const listFullSignInfo = [];
            const listFullCertPath = [];
            const listFullRequiredAttr = [];
            const listFullOptionalAttr = [];
            for (let index = 0; index < report.initialReport.qtdSignatures; index++) {

                listFullSignInfo.push([
                    { label: t('validated.signature-type'), value: signatures[index].signatureType },
                    signatures[index].certification.signer.validSignature ? { label: t('validated.signature-status'), value: signatures[index].certification.signer.validSignature } : null,
                    signatures[index].objectDictionarySignature ? { label: t('validated.signature-dictionary-object'), value: signatures[index].objectDictionarySignature.replace('<code>', '').replace('</code>', '') } : null,
                    { label: t('validated.certification-path'), value: signatures[index].certification.signer.certPathValid },
                    signatures[index].certification.signer.certPathMessage ? { label: t('validated.error-message'), value: signatures[index].certification.signer.certPathMessage } : null,
                    signatures[index].integrity.schemaPattern ? { label: t('validated.signature-structure'), value: `${t('validated.compliance-standard')}  (${signatures[index].integrity.schemaPattern})` } : null,
                    { label: t('validated.asymmetric-cipher'), value: signatures[index].integrity.asymmetricCipher },
                    { label: t('validated.cryptographic-digest'), value: signatures[index].integrity.hash },
                    { label: t('validated.signature-date'), value: signatures[index].signingTime },
                    (state.data.extended_report === 'true' && signatures[index].attributes && signatures[index].attributes.requiredAttributes) ? {
                        label: t('validated.attribute-status'), value:
                            (signatures[index].attributes.requiredAttributes.requiredAttribute.some(attr => attr.status !== 'Aprobado')) ?
                                (signatures[index].attributes.requiredAttributes.requiredAttribute.some(attr => attr.status === 'Indeterminado')) ?
                                    t('validated.exist-attr-indeterminated') : t('validated.exist-attr-reprobed')
                                : t('validated.approveds')
                    } : null,
                    signatures[index].warningMessages?.warningMessage ? { label: t('validated.alert-message'), value: signatures[index].warningMessages.warningMessage } : null,
                    signatures[index].errorMessages?.errorMessage ? { label: t('validated.error-message'), value: signatures[index].errorMessages.errorMessage } : null
                ]);

                let item = 1;
                const listCertPath = [];
                if (signatures[index].certification.signer.certificates) {
                    if (Array.isArray(signatures[index].certification.signer.certificates.certificate)) {
                        signatures[index].certification.signer.certificates.certificate.forEach(cert => {
                            listCertPath.push({
                                item: item,
                                key: cert.serialNumber,
                                title: cert.subjectName,
                                details: [
                                    { label: t('validated.issuer'), value: cert.issuerName },
                                    { label: t('validated.status'), value: cert.validSignature ? t('validated.approved') : t('validated.failed') },
                                    { label: t('validated.obtained'), value: cert.online ? t('validated.online') : t('validated.offline') },
                                    { label: t('validated.approved-since'), value: cert.notBefore },
                                    { label: t('validated.approved-until'), value: cert.notAfter }
                                ],
                                info: cert.hasCRLRevocation ? [
                                    { label: t('validated.certificate-status'), value: cert.hasCRLRevocation ? t('validated.valid') : t('validated.invalid') }
                                ] : null,
                                listRev: cert.crl ? (Array.isArray(cert.crl) ? cert.crl.map(crl => {
                                    return [
                                        { label: t('validated.signature'), value: crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                        { label: t('validated.obtained'), value: crl.online ? t('validated.online') : t('validated.offline') },
                                        { label: t('validated.publication-date'), value: crl.dates.thisUpdate },
                                        { label: t('validated.next-update'), value: crl.dates.nextUpdate }
                                    ]
                                }) : [[
                                    { label: t('validated.signature'), value: cert.crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                    { label: t('validated.obtained'), value: cert.crl.online ? t('validated.online') : t('validated.offline') },
                                    { label: t('validated.publication-date'), value: cert.crl.dates.thisUpdate },
                                    { label: t('validated.next-update'), value: cert.crl.dates.nextUpdate }
                                ]]) : null
                            });
                            item++;
                        });
                    } else {
                        let cert = signatures[index].certification.signer.certificates.certificate;
                        listCertPath.push({
                            item: item,
                            key: cert.serialNumber,
                            title: cert.subjectName,
                            details: [
                                { label: t('validated.issuer'), value: cert.issuerName },
                                { label: t('validated.status'), value: cert.validSignature ? t('validated.approved') : t('validated.failed') },
                                { label: t('validated.obtained'), value: cert.online ? t('validated.online') : t('validated.offline') },
                                { label: t('validated.approved-since'), value: cert.notBefore },
                                { label: t('validated.approved-until'), value: cert.notAfter }
                            ],
                            info: cert.hasCRLRevocation ? [
                                { label: t('validated.certificate-status'), value: cert.hasCRLRevocation ? t('validated.valid') : t('validated.invalid') }
                            ] : null,
                            listRev: cert.crl ? (Array.isArray(cert.crl) ? cert.crl.map(crl => {
                                return [
                                    { label: t('validated.signature'), value: crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                    { label: t('validated.obtained'), value: crl.online ? t('validated.online') : t('validated.offline') },
                                    { label: t('validated.publication-date'), value: crl.dates.thisUpdate },
                                    { label: t('validated.next-update'), value: crl.dates.nextUpdate }
                                ]
                            }) : [[
                                { label: t('validated.signature'), value: cert.crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                { label: t('validated.obtained'), value: cert.crl.online ? t('validated.online') : t('validated.offline') },
                                { label: t('validated.publication-date'), value: cert.crl.dates.thisUpdate },
                                { label: t('validated.next-update'), value: cert.crl.dates.nextUpdate }
                            ]]) : null
                        });
                    }
                }
                listFullCertPath.push(listCertPath);

                if (state.data.extended_report === 'true') {
                    const listRequiredAttr = [];
                    if (signatures[index].attributes && signatures[index].attributes.requiredAttributes) {
                        signatures[index].attributes.requiredAttributes.requiredAttribute.forEach(attr => {
                            listRequiredAttr.push({ label: attr.name, value: attr.status });
                        });
                        listFullRequiredAttr.push(listRequiredAttr);
                    } else {
                        listFullRequiredAttr.push([]);
                    }

                    if (signatures[index].attributes && signatures[index].attributes.optionalAttributes) {
                        if (Array.isArray(signatures[index].attributes.optionalAttributes.optionalAttribute)) {
                            signatures[index].attributes.optionalAttributes.optionalAttribute.forEach(attr => {
                                listFullOptionalAttr.push({ label: attr.name, value: attr.status });
                            });
                        } else {
                            listFullOptionalAttr.push([{
                                label: signatures[index].attributes.optionalAttributes.optionalAttribute.name,
                                value: signatures[index].attributes.optionalAttributes.optionalAttribute.status
                            }]);
                        }
                    } else {
                        listFullOptionalAttr.push([]);
                    }
                }
            }

            setTableSignInfo(listFullSignInfo);
            setTableCertPath(listFullCertPath);
            setTableRequiredAttr(listFullRequiredAttr);
            setTableOptionalAttr(listFullOptionalAttr);

        } else {
            if (signatures) {
                setTableSignInfo([[
                    { label: t('validated.signature-type'), value: signatures.signatureType },
                    signatures.certification.signer.validSignature ? { label: t('validated.signature-status'), value: signatures.certification.signer.validSignature } : null,
                    signatures.objectDictionarySignature ? { label: t('validated.signature-dictionary-object'), value: signatures.objectDictionarySignature.replace('<code>', '').replace('</code>', '') } : null,
                    { label: t('validated.certification-path'), value: signatures.certification.signer.certPathValid },
                    signatures.certification.signer.certPathMessage ? { label: t('validated.error-message'), value: signatures.certification.signer.certPathMessage } : null,
                    signatures.integrity.schemaPattern ? { label: t('validated.signature-structure'), value: `${t('validated.compliance-standard')}  (${signatures.integrity.schemaPattern})` } : null,
                    { label: t('validated.asymmetric-cipher'), value: signatures.integrity.asymmetricCipher },
                    { label: t('validated.cryptographic-digest'), value: signatures.integrity.hash },
                    { label: t('validated.signature-date'), value: signatures.signingTime },
                    (state.data.extended_report === 'true' && signatures.attributes && signatures.attributes.requiredAttributes) ? {
                        label: t('validated.attribute-status'), value:
                            (signatures.attributes.requiredAttributes.requiredAttribute.some(attr => attr.status !== 'Aprobado')) ?
                                (signatures.attributes.requiredAttributes.requiredAttribute.some(attr => attr.status === 'Indeterminado')) ?
                                    t('validated.exist-attr-indeterminated') : t('validated.exist-attr-reprobed')
                                : t('validated.approveds')
                    } : null,
                    signatures.warningMessages?.warningMessage ? { label: t('validated.alert-message'), value: signatures.warningMessages.warningMessage } : null,
                    signatures.errorMessages?.errorMessage ? { label: t('validated.error-message'), value: signatures.errorMessages.errorMessage } : null
                ]]);

                let item = 1;
                const listCertPath = [];
                if (signatures.certification.signer.certificates) {
                    if (Array.isArray(signatures.certification.signer.certificates.certificate)) {
                        signatures.certification.signer.certificates.certificate.forEach(cert => {
                            listCertPath.push({
                                item: item,
                                key: cert.serialNumber,
                                title: cert.subjectName,
                                details: [
                                    { label: t('validated.issuer'), value: cert.issuerName },
                                    { label: t('validated.status'), value: cert.validSignature ? t('validated.approved') : t('validated.failed') },
                                    { label: t('validated.obtained'), value: cert.online ? t('validated.online') : t('validated.offline') },
                                    { label: t('validated.approved-since'), value: cert.notBefore },
                                    { label: t('validated.approved-until'), value: cert.notAfter }
                                ],
                                info: cert.hasCRLRevocation ? [
                                    { label: t('validated.certificate-status'), value: cert.hasCRLRevocation ? t('validated.valid') : t('validated.invalid') }
                                ] : null,
                                listRev: cert.crl ? (Array.isArray(cert.crl) ? cert.crl.map(crl => {
                                    return [
                                        { label: t('validated.signature'), value: crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                        { label: t('validated.obtained'), value: crl.online ? t('validated.online') : t('validated.offline') },
                                        { label: t('validated.publication-date'), value: crl.dates.thisUpdate },
                                        { label: t('validated.next-update'), value: crl.dates.nextUpdate }
                                    ]
                                }) : [[
                                    { label: t('validated.signature'), value: cert.crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                    { label: t('validated.obtained'), value: cert.crl.online ? t('validated.online') : t('validated.offline') },
                                    { label: t('validated.publication-date'), value: cert.crl.dates.thisUpdate },
                                    { label: t('validated.next-update'), value: cert.crl.dates.nextUpdate }
                                ]]) : null
                            });
                            item++;
                        });
                    } else {
                        let cert = signatures.certification.signer.certificates.certificate;
                        listCertPath.push({
                            item: item,
                            key: cert.serialNumber,
                            title: cert.subjectName,
                            details: [
                                { label: t('validated.issuer'), value: cert.issuerName },
                                { label: t('validated.status'), value: cert.validSignature ? t('validated.approved') : t('validated.failed') },
                                { label: t('validated.obtained'), value: cert.online ? t('validated.online') : t('validated.offline') },
                                { label: t('validated.approved-since'), value: cert.notBefore },
                                { label: t('validated.approved-until'), value: cert.notAfter }
                            ],
                            info: cert.hasCRLRevocation ? [
                                { label: t('validated.certificate-status'), value: cert.hasCRLRevocation ? t('validated.valid') : t('validated.invalid') }
                            ] : null,
                            listRev: cert.crl ? (Array.isArray(cert.crl) ? cert.crl.map(crl => {
                                return [
                                    { label: t('validated.signature'), value: crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                    { label: t('validated.obtained'), value: crl.online ? t('validated.online') : t('validated.offline') },
                                    { label: t('validated.publication-date'), value: crl.dates.thisUpdate },
                                    { label: t('validated.next-update'), value: crl.dates.nextUpdate }
                                ]
                            }) : [[
                                { label: t('validated.signature'), value: cert.crl.validSignature ? t('validated.approved') : t('validated.failed') },
                                { label: t('validated.obtained'), value: cert.crl.online ? t('validated.online') : t('validated.offline') },
                                { label: t('validated.publication-date'), value: cert.crl.dates.thisUpdate },
                                { label: t('validated.next-update'), value: cert.crl.dates.nextUpdate }
                            ]]) : null
                        });
                    }
                }
                setTableCertPath([listCertPath]);

                if (state.data.extended_report === 'true') {
                    const listAttributes = [];
                    if (signatures.attributes && signatures.attributes.requiredAttributes) {
                        signatures.attributes.requiredAttributes.requiredAttribute.forEach(attr => {
                            listAttributes.push({ label: attr.name, value: attr.status });
                        });
                        setTableRequiredAttr([listAttributes]);
                    } else {
                        setTableRequiredAttr([[]]);
                    }

                    if (signatures.attributes && signatures.attributes.optionalAttributes) {
                        if (Array.isArray(signatures.attributes.optionalAttributes.optionalAttribute)) {
                            const listOpAttributes = [];
                            signatures.attributes.optionalAttributes.optionalAttribute.forEach(attr => {
                                listOpAttributes.push({ label: attr.name, value: attr.status });
                            });
                            setTableOptionalAttr([listOpAttributes]);
                        } else {
                            setTableOptionalAttr([[{
                                label: signatures.attributes.optionalAttributes.optionalAttribute.name,
                                value: signatures.attributes.optionalAttributes.optionalAttribute.status
                            }]]);
                        }
                    } else {
                        setTableOptionalAttr([[]]);
                    }
                }
            }
        }
    }

    const loadPartialData = (state) => {
        const { report } = state;
        setTableAprovedSign([
            { label: t('validated.verification-date'), value: report.date.verificationDate },
            { label: t('validated.software-version'), value: report.software.version }
        ]);

        setTableFileInfo([
            { label: t('validated.file-name'), value: report.software.sourceFile },
            { label: t('validated.summary-sha256'), value: report.software.sourceFileHash },
            { label: t('validated.file-type'), value: report.initialReport.fileType },
            { label: t('validated.number-signatures'), value: report.initialReport.qtdSignatures },
            { label: t('validated.number-pinned-signatures'), value: report.initialReport.qtdAnchorsSign }
        ]);
    }

    const onNavigateClose = () => {
        navigate('/', { replace: true });
    }

    const onNavigateBack = () => {
        navigate('/validator', {
            replace: true,
            state: { file: state.file, data: state.data, fileBlob: state.fileBlob }
        });
    }

    const onDownloadDoc = async () => {
        try {
            if (state.fileBlob) {
                const binary = atob(state.receiptBase64.split(',')[1]);
                const array = [];
                for (let i = 0; i < binary.length; i++) {
                    array.push(binary.charCodeAt(i));
                }
                const fileBlob = new Blob([new Uint8Array(array)], { type: state.receiptBase64.split(',')[0] });
                FileSaver.saveAs(fileBlob, state.receiptFilename);
            }
        } catch (error) {
            console.error('Failed to validated the file:', error);
        }
    }

    useEffect(() => {
        if (state) {
            if (state.report) {
                switch (state.report.generalStatus) {
                    case 'Aprobado':
                    case 'Indeterminado':
                    case 'Reprobado':
                        loadFullData(state);
                        break;
                    case 'Inválido':
                        loadPartialData(state);
                        break;
                    default:
                        break;
                }
            }
            setTimeout(function () {
                window.collapseContent();
            }, 100);
        } else {
            navigate('/validator');
        }
    }, [state]);

    return state && (
        <main className="main-content main-full">

            <div className="component-main-content-firma">
                <div className="page-container">

                    {/* Card content */}
                    <div className="card-container">

                        <div className="card-head">
                            {/* Titulo de seccion */}
                            <h1 className="card-title">
                                <img src={iconValidar} className="img-fluid" alt={t('validator.title')} />
                                {t('validator.title')}
                            </h1>
                            {/* Head actions */}
                            <div className="head-actions">
                                <button onClick={onNavigateClose} className="btn-small btn-primary-outline"> {t('validated.close')} </button>
                                <button onClick={onNavigateBack} className="btn-small btn-primary"> <span className="material-symbols-outlined"> chevron_backward </span> {t('validated.back')} </button>
                                <button onClick={onDownloadDoc} className="btn-small btn-primary"> <span className="material-symbols-outlined"> download </span> {t('validated.download')} </button>
                            </div>
                        </div>

                        {/* card-grid */}
                        {state.report && (<div className="card-grid card-full">
                            {/* alert de documento */}
                            <div className="component-alert">
                                <div className="alert alert-success-alt">
                                    <span className="material-symbols-outlined"> check_circle </span>
                                    <p> {t('validated.document-loaded')} <span> {state.file.name} </span> </p>
                                </div>
                            </div>

                            <div className="component-collapse-validacion">

                                {/* collapse wrapper */}
                                <div className="collapse-wrapper">
                                    <h3> {t('validated.validation-report')} </h3>

                                    {/* Alert  */}
                                    {!state.report.generalStatus && (<div className="component-alert">
                                        <div className="alert alert-info">
                                            <span className="material-icons"> info </span>
                                            <p> {t('validated.info-validation-report')} </p>
                                        </div>
                                    </div>)}

                                    {/* collapse item 1 */}
                                    {state.report.generalStatus && (<div className="collapse-item show" tabIndex="0">
                                        {state.report.generalStatus === 'Aprobado' && (<a href="#item1" className="collapse-item-heading heading-aprobado" tabIndex="0" role="button">
                                            <div className="material-symbols-outlined"> check_circle </div>
                                            {t('validated.title-approved-sign')}
                                        </a>)}
                                        {state.report.generalStatus === 'Indeterminado' && (<a href="#item1" className="collapse-item-heading heading-indeterminado" tabIndex="0" role="button">
                                            <div className="material-symbols-outlined"> warning_amber </div>
                                            {t('validated.title-indeterminate-sign')}
                                        </a>)}
                                        {state.report.generalStatus === 'Inválido' && (<a href="#item1" className="collapse-item-heading heading-novalido" tabIndex="0" role="button">
                                            <div className="material-symbols-outlined"> cancel </div>
                                            {t('validated.title-invalid-sign')}
                                        </a>)}
                                        {state.report.generalStatus === 'Reprobado' && (<a href="#item1" className="collapse-item-heading heading-rechazado" tabIndex="0" role="button">
                                            <div className="material-symbols-outlined"> cancel </div>
                                            {t('validated.title-refused-sign')}
                                        </a>)}

                                        <div id="item1" className="collapse-item-content">
                                            {/* tabla */}
                                            <TableList key="tableApprovedSign" dataTable={tableApprovedSign} />
                                            {/* tabla */}

                                            {/* collapse subitem 1 */}
                                            <div className="collapse-item" tabIndex="0">
                                                <a href="#item2" className="collapse-item-heading" tabIndex="0" role="button">
                                                    {t('validated.title-file-info')}
                                                </a>

                                                <div id="item2" className="collapse-item-content">
                                                    {/* tabla */}
                                                    <TableList key="tableFileInfo" dataTable={tableFileInfo} />
                                                    {/* tabla */}
                                                </div>
                                            </div>
                                            {/* collapse subitem 1 */}

                                            {/* collapse subitem 2 */}
                                            {tableListInfo.length > 0 && (
                                                <div className="collapse-item" tabIndex="0">
                                                    <a href="#item3" className="collapse-item-heading" tabIndex="0" role="button">
                                                        {t('validated.title-list-info')}
                                                    </a>
                                                    <div id="item3" className="collapse-item-content">
                                                        {/* collapse subitem 3 */}
                                                        {tableListInfo.map((listInfo, index) => (
                                                            <div key={index} className="collapse-item" tabIndex="0">
                                                                <a href="#item4" className="collapse-item-heading" tabIndex="0" role="button">
                                                                    {(Array.isArray(state.report.tls.trustedList)) ?
                                                                        state.report.tls.trustedList[index].countryName :
                                                                        state.report.tls.trustedList.countryName}
                                                                </a>

                                                                <div id="item4" className="collapse-item-content">
                                                                    {/* tabla */}
                                                                    <TableList key="tableListInfo" dataTable={tableListInfo[index]} />
                                                                    {/* tabla */}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {/* collapse subitem 3 */}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* collapse subitem 2 */}

                                            {tableSignInfo.map((signInfo, index) => (
                                                <div key={index} className="collapse-item" tabIndex="0">
                                                    <a href="#item5" tabIndex="0" role="button"
                                                        className={`collapse-item-heading ${state.report.initialReport.qtdSignatures === 1 ?
                                                            (state.report.signatures.signature.certification.signer.validSignature === 'Reprobado' ||
                                                                state.report.signatures.signature.certification.signer.validSignature === 'Indeterminada' ?
                                                                'heading-indeterminado' : '') :
                                                            (state.report.signatures.signature[index].certification.signer.validSignature === 'Reprobado' ||
                                                                state.report.signatures.signature[index].certification.signer.validSignature === 'Indeterminada' ?
                                                                'heading-indeterminado' : '')}`}>
                                                        {state.report.initialReport.qtdSignatures === 1 ?
                                                            state.report.signatures.signature.certification.signer.flagCountry :
                                                            state.report.signatures.signature[index].certification.signer.flagCountry}
                                                        {t('validated.title-signature-of')}
                                                        {state.report.initialReport.qtdSignatures === 1 ?
                                                            state.report.signatures.signature.certification.signer.subjectName :
                                                            state.report.signatures.signature[index].certification.signer.subjectName}
                                                    </a>
                                                    <div id="item5" className="collapse-item-content">

                                                        {/* collapse subitem 6 */}
                                                        <div className="collapse-item" tabIndex="0">
                                                            <a href="#item6" className="collapse-item-heading" tabIndex="0" role="button">
                                                                {t('validated.title-signature-info')}
                                                            </a>

                                                            <div id="item6" className="collapse-item-content">
                                                                {/* tabla */}
                                                                <TableList key="tableSignInfo" dataTable={tableSignInfo[index]} />
                                                                {/* tabla */}
                                                            </div>
                                                        </div>
                                                        {/* collapse subitem 6 */}

                                                        {/* collapse subitem 7 */}
                                                        {tableCertPath[index].length > 0 && (<div className="collapse-item" tabIndex="0">
                                                            <a href="#item7" className="collapse-item-heading" tabIndex="0" role="button">
                                                                {t('validated.certification-path')}
                                                            </a>
                                                            <div id="item7" className="collapse-item-content">

                                                                {/* collapse subitems */}
                                                                {
                                                                    tableCertPath[index].map(cert => (
                                                                        <div key={cert.key} className="collapse-item" tabIndex="0">
                                                                            <a href={`#itemCert${cert.item}`} className="collapse-item-heading" tabIndex="0" role="button">
                                                                                {cert.title}
                                                                            </a>
                                                                            <div id={`itemCert${cert.item}`} className="collapse-item-content">
                                                                                {/* tabla */}
                                                                                <TableList key={`tableCert${cert.item}`} dataTable={cert.details} />
                                                                                {/* tabla */}

                                                                                {/* collapse subitem */}
                                                                                {cert.info && (<div className="collapse-item" tabIndex="0">
                                                                                    <a href={`#itemInfo${cert.item}`} className="collapse-item-heading" tabIndex="0" role="button">
                                                                                        {t('validated.revocation-info')}
                                                                                    </a>

                                                                                    <div id={`itemInfo${cert.item}`} className="collapse-item-content">
                                                                                        {/* tabla */}
                                                                                        <TableList key={`tableInfo${cert.item}`} dataTable={cert.info} />
                                                                                        {/* tabla */}
                                                                                    </div>
                                                                                </div>)}
                                                                                {/* collapse subitem */}

                                                                                {/* collapse subitem */}
                                                                                {cert.listRev && (cert.listRev.map(rev => (<div key={rev} className="collapse-item" tabIndex="0">
                                                                                    <a href={`#itemRev${cert.item}`} className="collapse-item-heading" tabIndex="0" role="button">
                                                                                        {t('validated.certificate-revocation')}
                                                                                    </a>

                                                                                    <div id={`itemRev${cert.item}`} className="collapse-item-content">
                                                                                        {/* tabla */}
                                                                                        <TableList key={`tableRev${cert.item}`} dataTable={rev} />
                                                                                        {/* tabla */}
                                                                                    </div>
                                                                                </div>)))}
                                                                                {/* collapse subitem */}

                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                }
                                                                {/* collapse subitems */}

                                                            </div>
                                                        </div>)}
                                                        {/* collapse subitem 7 */}

                                                        {/* collapse subitem 8 */}
                                                        {state.data.extended_report === 'true' && (tableRequiredAttr[index].length > 0 || tableOptionalAttr[index].length > 0) && (<div className="collapse-item" tabIndex="0">
                                                            <a href="#item8" className="collapse-item-heading" tabIndex="0" role="button">
                                                                {t('validated.subscription-attr')}
                                                            </a>
                                                            <div id="item8" className="collapse-item-content">

                                                                {/* collapse subitem 9 */}
                                                                {tableRequiredAttr[index].length > 0 && (<div className="collapse-item" tabIndex="0">
                                                                    <a href="#item9" className="collapse-item-heading" tabIndex="0" role="button">
                                                                        {t('validated.required-attr')}
                                                                    </a>
                                                                    <div id="item9" className="collapse-item-content">
                                                                        {/* tabla */}
                                                                        <TableList key="tableRequiredAttr" dataTable={tableRequiredAttr[index]} />
                                                                        {/* tabla */}
                                                                    </div>
                                                                </div>)}
                                                                {/* collapse subitem 9 */}

                                                                {/* collapse subitem 10 */}
                                                                {tableOptionalAttr[index].length > 0 && (<div className="collapse-item" tabIndex="0">
                                                                    <a href="#item10" className="collapse-item-heading" tabIndex="0" role="button">
                                                                        {t('validated.optional-attr')}
                                                                    </a>
                                                                    <div id="item10" className="collapse-item-content">
                                                                        {/* tabla */}
                                                                        <TableList key="tableOptionalAttr" dataTable={tableOptionalAttr[index]} />
                                                                        {/* tabla */}
                                                                    </div>
                                                                </div>)}
                                                                {/* collapse subitem 10 */}

                                                            </div>
                                                        </div>)}
                                                        {/* collapse subitem 8 */}

                                                    </div>
                                                </div>
                                            ))}

                                        </div>
                                    </div>)}
                                    {/* collapse item 1 */}

                                </div>
                                {/* collapse wrapper */}

                            </div>

                        </div>)}

                    </div>
                    {/* Card content */}

                </div>
            </div>

        </main >
    )
}
